import { useState, useEffect } from 'react';
import { ref, onValue, off, remove, get } from 'firebase/database';
import { database as db, auth } from '../../firebaseConfig';
// ⚠️ REMOVED: applySpaceSettingsToWidgetSync - Templates should NOT be styled!
// Templates preserve their exact original design (colors, gradients, backgrounds)

/**
 * Custom Hook für Custom Widget Templates
 * Lädt und verwaltet Custom Templates aus Firebase:
 * - Templates vom Global Template User (nxm4h3KUFAPwLBQQ9aL2j6FK4jr2) - für ALLE sichtbar
 * - Templates vom aktuell eingeloggten User - nur für diesen User sichtbar
 * Nur eigene Templates können gelöscht werden
 */
export const useCustomTemplates = (spaceId = null) => {
    const [customTemplates, setCustomTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // 🌍 Global Template User ID - Templates von diesem User sind für ALLE sichtbar
    const GLOBAL_TEMPLATE_USER_ID = 'nxm4h3KUFAPwLBQQ9aL2j6FK4jr2';

    // ⚠️ REMOVED: loadSpaceSettings - Templates should preserve their EXACT original colors!
    // No color transformation or space settings application for templates

    useEffect(() => {
        const user = auth.currentUser;
        if (!user) {
            setLoading(false);
            setError('User not authenticated');
            return;
        }

        console.log('🎨 Loading custom templates for user:', user.uid);

        // 🎨 Firebase Listeners für Custom Templates (NO color transformation!)
        const initializeTemplates = async () => {
            // ⚠️ Templates preserve original design - NO space settings applied!
            // 1. Global Templates (für ALLE sichtbar)
            // 2. User Templates (nur für aktuellen User sichtbar)
            
            let templatesArray = [];
            let globalTemplatesLoaded = false;
            let userTemplatesLoaded = false;
            
            const checkAndUpdateTemplates = () => {
                // Warte bis beide Queries geladen sind
                if (!globalTemplatesLoaded || !userTemplatesLoaded) return;
                
                // ⚠️ CRITICAL: DO NOT apply space settings to templates!
                // Templates should preserve their EXACT original design (colors, gradients, backgrounds)
                // applySpaceSettingsToWidgetSync würde backgroundColor überschreiben und gradient properties entfernen!
                console.log('✅ Loaded', templatesArray.length, 'custom templates WITHOUT color override');
                console.log('🎨 Templates preserve original Widget-Level & Container-Level backgrounds');

                // Sort by creation date (newest first)
                templatesArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                console.log('✅ Custom templates loaded and styled:', templatesArray.length);
                setCustomTemplates(templatesArray);
                setLoading(false);
                setError(null);
            };
            
            // 🌍 Listener 1: Global Templates
            const globalTemplatesRef = ref(db, `customWidgetTemplates/${GLOBAL_TEMPLATE_USER_ID}`);
            
            const handleGlobalTemplatesUpdate = (snapshot) => {
                try {
                    // Reset global templates
                    templatesArray = templatesArray.filter(t => t.ownerId !== GLOBAL_TEMPLATE_USER_ID);
                    
                    if (snapshot.exists()) {
                        const globalTemplatesData = snapshot.val();
                        
                        // Add global templates to array
                        Object.keys(globalTemplatesData).forEach(templateId => {
                            templatesArray.push({
                                ...globalTemplatesData[templateId],
                                templateId: templateId,
                                ownerId: GLOBAL_TEMPLATE_USER_ID,
                                isGlobalTemplate: true // Flag für UI
                            });
                        });
                        
                        console.log('🌍 Global templates loaded:', Object.keys(globalTemplatesData).length);
                    } else {
                        console.log('📭 No global templates found');
                    }
                    
                    globalTemplatesLoaded = true;
                    checkAndUpdateTemplates();
                } catch (err) {
                    console.error('❌ Error loading global templates:', err);
                    globalTemplatesLoaded = true;
                    checkAndUpdateTemplates();
                }
            };
            
            // 👤 Listener 2: Current User Templates
            const userTemplatesRef = ref(db, `customWidgetTemplates/${user.uid}`);
            
            const handleUserTemplatesUpdate = (snapshot) => {
                try {
                    // Reset user templates
                    templatesArray = templatesArray.filter(t => t.ownerId !== user.uid);
                    
                    if (snapshot.exists()) {
                        const userTemplatesData = snapshot.val();
                        
                        // Add user templates to array
                        Object.keys(userTemplatesData).forEach(templateId => {
                            templatesArray.push({
                                ...userTemplatesData[templateId],
                                templateId: templateId,
                                ownerId: user.uid,
                                isGlobalTemplate: false
                            });
                        });
                        
                        console.log('👤 User templates loaded:', Object.keys(userTemplatesData).length);
                    } else {
                        console.log('📭 No user templates found');
                    }
                    
                    userTemplatesLoaded = true;
                    checkAndUpdateTemplates();
                } catch (err) {
                    console.error('❌ Error loading user templates:', err);
                    userTemplatesLoaded = true;
                    checkAndUpdateTemplates();
                }
            };

            // Attach both listeners
            onValue(globalTemplatesRef, handleGlobalTemplatesUpdate);
            onValue(userTemplatesRef, handleUserTemplatesUpdate);

            // Return cleanup function - cleanup both listeners
            return () => {
                off(globalTemplatesRef, 'value', handleGlobalTemplatesUpdate);
                off(userTemplatesRef, 'value', handleUserTemplatesUpdate);
            };
        };

        // Initialize templates and return cleanup
        const cleanupPromise = initializeTemplates();
        
        // Cleanup listener on unmount
        return () => {
            cleanupPromise.then(cleanup => cleanup && cleanup());
        };
    }, [spaceId]); // Re-run when spaceId changes

    // Filter templates by widget type category
    const getTemplatesByCategory = (category) => {
        return customTemplates.filter(template => template.widgetType === category);
    };

    // Filter templates by original widget type
    const getTemplatesByOriginalType = (originalType) => {
        return customTemplates.filter(template => template.originalWidgetType === originalType);
    };

    // Get template statistics
    const getTemplateStats = () => {
        const stats = {
            total: customTemplates.length,
            byCategory: {},
            byOriginalType: {}
        };

        customTemplates.forEach(template => {
            // Count by category
            stats.byCategory[template.widgetType] = (stats.byCategory[template.widgetType] || 0) + 1;
            
            // Count by original type
            stats.byOriginalType[template.originalWidgetType] = (stats.byOriginalType[template.originalWidgetType] || 0) + 1;
        });

        return stats;
    };

    // Delete template function - only owner can delete
    const deleteTemplate = async (templateId) => {
        const user = auth.currentUser;
        if (!user) {
            throw new Error('User not authenticated');
        }

        // Find the template to get the owner ID
        const template = customTemplates.find(t => t.templateId === templateId);
        if (!template) {
            throw new Error('Template not found');
        }

        // Check if current user is the owner
        if (template.ownerId !== user.uid) {
            throw new Error('Only the template creator can delete this template');
        }

        try {
            const templateRef = ref(db, `customWidgetTemplates/${template.ownerId}/${templateId}`);
            await remove(templateRef);
            console.log('✅ Template deleted:', templateId);
        } catch (error) {
            console.error('❌ Error deleting template:', error);
            throw error;
        }
    };

    // Check if current user can delete a template
    const canDeleteTemplate = (templateId) => {
        const user = auth.currentUser;
        if (!user) return false;
        
        const template = customTemplates.find(t => t.templateId === templateId);
        return template && template.ownerId === user.uid;
    };

    return {
        customTemplates,
        loading,
        error,
        getTemplatesByCategory,
        getTemplatesByOriginalType,
        getTemplateStats,
        deleteTemplate,
        canDeleteTemplate
    };
};
